import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet';

import { SiteSeoFragment } from '../../../graphql-types';

type MetaProp = JSX.IntrinsicElements['meta'];

interface SeoProps {
  description?: string;
  lang?: string;
  meta?: MetaProp[];
  title?: string;
  image?: string;
  slug?: string;
  keywords?: string;
}

const SEO: React.FC<SeoProps> = ({
  description = '',
  lang = 'en',
  meta = [] as MetaProp[],
  title,
  image,
  slug,
  keywords,
}) => {
  const queryResult = useStaticQuery<SiteSeoFragment>(graphql`
    query {
      ...SiteSeo
    }
  `);
  const siteSeo = queryResult?.site?.siteMetadata;
  const siteTitle = siteSeo?.defaultTitle || 'WoodmenLife';
  const titleTemplate = `%s | ${siteTitle}`;
  const metaDescription = description || siteSeo?.defaultDescription!;
  const metaKeywords = keywords;
  const siteUrl = `${siteSeo?.siteUrl || 'https://www.woodmenlife.org'}`;
  const canonicalUrl = `${siteUrl}${slug}`;
  const defaultImage = queryResult?.featuredImage?.publicURL;
  const pageImage = image || defaultImage || undefined;
  const jsonLd = queryResult?.site?.siteMetadata?.jsonLd;
  const baseMetaProps: MetaProp[] = [
    {
      name: `keywords`,
      content: metaKeywords,
    },
    {
      name: `description`,
      content: metaDescription,
    },
    {
      name: `image`,
      content: pageImage,
    },
  ];
  //`og:*` === OpenGraph, controls how this page is shown when shared on social media (namely Facebook & LinkedIn)
  const openGraphProps: MetaProp[] = [
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:site_name`,
      content: title,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:image`,
      content: pageImage,
    },
    {
      property: `og:type`,
      content: 'website',
    },
    {
      property: `og:url`,
      content: canonicalUrl,
    },
  ];
  const facebookProps: MetaProp[] = [
    {
      name: 'fb:app_id',
      content: siteSeo?.facebookAppId || undefined,
    },
  ];
  const twitterProps: MetaProp[] = [
    {
      name: 'twitter:site',
      content: siteSeo?.twitterUsername || undefined,
    },
    {
      name: 'twitter:creator',
      content: siteSeo?.twitterUsername || undefined,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: 'twitter:image',
      content: pageImage,
    },
  ];

  const metaProps = [
    ...baseMetaProps,
    ...openGraphProps,
    ...facebookProps,
    ...twitterProps,
    ...meta,
  ];

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      defaultTitle={siteTitle}
      titleTemplate={titleTemplate}
      meta={metaProps}
    >
      {jsonLd && <script type="application/ld+json">{jsonLd}</script>}
    </Helmet>
  );
};

export default SEO;
